"use client";

import { c as _c } from "react/compiler-runtime";
import React from "react";
import { useArticleData } from "../../../context/ArticleDataContext";
import { ArticlePublishedInfo } from "../../shared/ArticlePublishedInfo";
import type { Media } from "@/payload-types";
import type { LocaleCode } from "@/lib/labels/types";
import { getMediaUrl } from "@/utilities/media/getMediaUrl";
import { MediaCredit } from "@/components/MediaCredit/MediaCredit";
import { MediaCaption } from "@/components/MediaCaption/MediaCaption";
import { FillImage } from "@/components/ui/FillImage";
import { MediaVariantsProvider } from "@/components/ui/MediaVariantsProvider";

/**
 * Default article header variant.
 *
 * Content-only component — the branded gradient background is provided by the
 * wrapping SectionStrip in the article layout. Text colors inherit from the
 * SectionStrip's CSS variables (--foreground, --muted-foreground).
 *
 * Features:
 * - Image on the left with rounded corners
 * - Title, subtitle, category badge, and publish date on the right
 */
export function ArticleHeaderDefault() {
  const $ = _c(25);
  const {
    post,
    primaryCategory,
    locale: localeStr
  } = useArticleData();
  const locale = localeStr as LocaleCode;
  const heroImage = post.heroImage as Media | null;
  let t0;
  if ($[0] !== primaryCategory?.title) {
    t0 = primaryCategory?.title?.toUpperCase() || "";
    $[0] = primaryCategory?.title;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const categoryName = t0;
  let t1;
  if ($[2] !== heroImage || $[3] !== post.title) {
    t1 = heroImage?.url && <div className="lg:w-1/2"><div className="relative aspect-video rounded-lg overflow-hidden"><MediaVariantsProvider media={heroImage} sizes="(max-width: 1024px) 100vw, 50vw" priority={true}><FillImage src={getMediaUrl(heroImage, "large") || heroImage.url} alt={heroImage.alt || post.title} className="object-cover" /></MediaVariantsProvider></div><MediaCaption media={heroImage} /><MediaCredit media={heroImage} className="mt-1 text-right" /></div>;
    $[2] = heroImage;
    $[3] = post.title;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== categoryName) {
    t2 = categoryName && <span className="inline-block px-3 py-1 text-xs font-bold rounded bg-foreground/20 text-inherit">{categoryName}</span>;
    $[5] = categoryName;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== locale || $[8] !== post.publishedAt || $[9] !== post.updatedAt) {
    t3 = post.publishedAt && <ArticlePublishedInfo publishedAt={post.publishedAt} updatedAt={post.updatedAt} locale={locale} className="text-sm text-inherit opacity-70" />;
    $[7] = locale;
    $[8] = post.publishedAt;
    $[9] = post.updatedAt;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t2 || $[12] !== t3) {
    t4 = <div className="flex flex-col items-start lg:flex-row lg:items-center gap-3 mb-4">{t2}{t3}</div>;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== post.title) {
    t5 = <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 leading-tight">{post.title}</h1>;
    $[14] = post.title;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== post.summary) {
    t6 = post.summary && <p className="text-base md:text-lg leading-relaxed text-inherit opacity-80">{post.summary}</p>;
    $[16] = post.summary;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t4 || $[19] !== t5 || $[20] !== t6) {
    t7 = <div className="flex-1 p-6 lg:p-12 flex flex-col justify-center">{t4}{t5}{t6}</div>;
    $[18] = t4;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== t1 || $[23] !== t7) {
    t8 = <header className="relative w-full py-6 md:py-8"><div className="max-w-350 mx-auto px-4"><div className="flex flex-col lg:flex-row gap-6 lg:gap-8">{t1}{t7}</div></div></header>;
    $[22] = t1;
    $[23] = t7;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  return t8;
}